import React from 'react';
import { Link } from 'react-router-dom';
import { Package, ArrowLeft } from 'lucide-react';
import PartnerBadges from '../components/PartnerBadges';

/**
 * PartnerDetailContent — Rendu visuel pur de la fiche Partenaire.
 * 
 * Utilisé par :
 * - PartnerPage.js (mode="page", dans RetailerLayout avec navigation)
 * - PartnerPreviewSandbox.js (mode="preview", aperçu isolé Admin)
 * 
 * Props :
 * - partner : objet partenaire complet
 * - slug : slug du partenaire (pour les liens catalogue)
 * - mode : "page" (défaut) ou "preview"
 * - onBack : callback pour le bouton retour (mode page)
 */

const ZONE_LABELS = {
  france_metropolitaine: 'France métropolitaine',
  belgique: 'Belgique',
  suisse: 'Suisse',
  luxembourg: 'Luxembourg',
  dom_tom: 'DOM-TOM',
  europe: 'Europe',
  international: 'International',
};

const PartnerDetailContent = ({ partner, slug, mode = 'page', onBack }) => {
  if (!partner) return null;

  const {
    nom, name, description, logo, banniere,
    conditions_achat, contact_email, contact_telephone, contacts,
    process_commande, shipping_rules, payment_terms,
    precommandes_autorisees, rupture_commandable, livraison_partielle,
    supplier_type, is_partner: isPartnerCommercial,
  } = partner;

  const displayName = nom || name || 'Partenaire';
  const isPreview = mode === 'preview';

  const displayContacts = contacts?.length > 0
    ? contacts
    : (contact_email || contact_telephone)
      ? [{ id: '1', nom: 'Contact', email: contact_email, telephone: contact_telephone, fonction: null }]
      : [];

  const rules = shipping_rules || [];

  return (
    <div className="min-h-screen bg-[#F7F5F2]" data-testid="partner-detail-content">

      {/* ══════════ HERO ══════════ */}
      <section className="bg-white border-b border-gray-200">
        {banniere && (
          <div className="w-full h-44 sm:h-52 lg:h-64 overflow-hidden relative">
            <img src={banniere} alt={`Bannière ${displayName}`} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className={`bg-white rounded-lg shadow-sm border border-gray-100 p-5 sm:p-6 ${banniere ? '-mt-12 sm:-mt-16 relative z-10' : 'mt-6'}`}>
            <div className="flex flex-col sm:flex-row items-start gap-5 sm:gap-6">
              {logo && (
                <div className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-white border-2 border-gray-100 shadow-sm p-3 flex items-center justify-center">
                  <img src={logo} alt={`Logo ${displayName}`} className="max-w-full max-h-full object-contain" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight mb-2">{displayName}</h1>
                <PartnerBadges supplierType={supplier_type} capabilities={partner.capabilities} isPartner={isPartnerCommercial} size="md" className="mb-3" />
                {description && (
                  <p className="text-base text-gray-600 whitespace-pre-line leading-relaxed" style={{ maxWidth: '65ch' }}>{description}</p>
                )}
                {!isPreview && (
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <Link to={`/partner/${slug}/products`} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors shadow-sm" data-testid="view-products-cta">
                      <Package className="h-4 w-4" /> Voir le catalogue
                    </Link>
                    {onBack && (
                      <button onClick={onBack} className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 font-medium transition-colors" data-testid="back-btn">
                        <ArrowLeft className="h-4 w-4" /> Retour
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="h-6 sm:h-8" />
      </section>

      {/* ══════════ MAIN CONTENT ══════════ */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">

        {/* ── Conditions de livraison ── */}
        {rules.length > 0 && (
          <div className="mb-8">
            <div className="hidden md:block bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-testid="shipping-rules-table">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700">Conditions de livraison</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      <th className="text-left px-4 py-2 text-[10px] font-bold text-gray-500 uppercase">Zone</th>
                      <th className="text-right px-4 py-2 text-[10px] font-bold text-gray-500 uppercase">Min. commande</th>
                      <th className="text-right px-4 py-2 text-[10px] font-bold text-gray-500 uppercase">Franco</th>
                      <th className="text-right px-4 py-2 text-[10px] font-bold text-gray-500 uppercase">Frais de port</th>
                      <th className="text-right px-4 py-2 text-[10px] font-bold text-gray-500 uppercase">Délai d'expédition moyen*</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rules.map((rule, ri) => {
                      const zoneLabel = ZONE_LABELS[rule.zone] || rule.zone || 'Zone inconnue';
                      const frais = rule.frais_port_fixe != null ? `${rule.frais_port_fixe.toFixed(2)}€` : rule.frais_port_percent != null ? `${rule.frais_port_percent}%` : '—';
                      return (
                        <tr key={rule.id || ri} className="hover:bg-gray-50/50" data-testid={`zone-row-${ri}`}>
                          <td className="px-4 py-2.5 font-medium text-gray-900">{zoneLabel}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{rule.minimum_commande != null ? `${rule.minimum_commande}€` : '—'}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{rule.franco != null ? `${rule.franco}€` : '—'}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{frais}</td>
                          <td className="px-4 py-2.5 text-right text-gray-700">{rule.delai_expedition_jours != null ? `${rule.delai_expedition_jours}j` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-2 border-t border-gray-100">
                <p className="text-[10px] text-gray-400 italic">* délai moyen entre la validation de la commande par le distributeur et son expédition.</p>
              </div>
            </div>

            <div className="md:hidden space-y-3" data-testid="shipping-rules-mobile">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Conditions de livraison</h3>
              {rules.map((rule, ri) => {
                const zoneLabel = ZONE_LABELS[rule.zone] || rule.zone || 'Zone inconnue';
                const frais = rule.frais_port_fixe != null ? `${rule.frais_port_fixe.toFixed(2)}€` : rule.frais_port_percent != null ? `${rule.frais_port_percent}%` : '—';
                return (
                  <div key={rule.id || ri} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4" data-testid={`zone-card-mobile-${ri}`}>
                    <p className="text-sm font-bold text-gray-900 mb-2">{zoneLabel}</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                      <div><span className="text-gray-500">Min. commande</span><p className="font-semibold text-gray-900">{rule.minimum_commande != null ? `${rule.minimum_commande}€` : '—'}</p></div>
                      <div><span className="text-gray-500">Franco</span><p className="font-semibold text-gray-900">{rule.franco != null ? `${rule.franco}€` : '—'}</p></div>
                      <div><span className="text-gray-500">Frais de port</span><p className="font-semibold text-gray-900">{frais}</p></div>
                      <div><span className="text-gray-500">Délai expédition*</span><p className="font-semibold text-gray-900">{rule.delai_expedition_jours != null ? `${rule.delai_expedition_jours}j` : '—'}</p></div>
                    </div>
                  </div>
                );
              })}
              <p className="text-[10px] text-gray-400 italic">* délai moyen entre la validation de la commande par le distributeur et son expédition.</p>
            </div>
          </div>
        )}

        {/* ── Indicateurs distributeur ── */}
        {(precommandes_autorisees || rupture_commandable || livraison_partielle) && (
          <div className="flex flex-wrap justify-center gap-3 mb-8" data-testid="partner-capabilities">
            {precommandes_autorisees && (
              <div className="bg-emerald-50 rounded-lg border border-emerald-200 px-4 py-2.5 flex items-center gap-2 text-sm font-medium text-emerald-700">
                <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" /> Précommandes acceptées
              </div>
            )}
            {rupture_commandable && (
              <div className="bg-blue-50 rounded-lg border border-blue-200 px-4 py-2.5 flex items-center gap-2 text-sm font-medium text-blue-700">
                <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" /> Rupture commandable
              </div>
            )}
            {livraison_partielle && (
              <div className="bg-amber-50 rounded-lg border border-amber-200 px-4 py-2.5 flex items-center gap-2 text-sm font-medium text-amber-700">
                <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" /> Livraison partielle
              </div>
            )}
          </div>
        )}

        {/* ── Conditions d'achat ── */}
        {conditions_achat && (
          <section className="mb-6">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50"><h2 className="text-lg font-semibold text-gray-900">Conditions d'achat</h2></div>
              <div className="px-5 py-4"><p className="text-sm sm:text-base text-gray-700 whitespace-pre-line leading-relaxed">{conditions_achat}</p></div>
            </div>
          </section>
        )}

        {/* ── Conditions de paiement — même structure tableau que Conditions de livraison ── */}
        {payment_terms && (payment_terms.delai_jours || payment_terms.mode_paiement || payment_terms.escompte_percent || payment_terms.encours_max) && (
          <section className="mb-8">
            {/* Desktop — tableau */}
            <div className="hidden md:block bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-testid="payment-terms-card">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700">Conditions de paiement</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      {payment_terms.mode_paiement && <th className="text-left px-4 py-2 text-[10px] font-bold text-gray-500 uppercase">Mode de paiement</th>}
                      {payment_terms.delai_jours && <th className="text-right px-4 py-2 text-[10px] font-bold text-gray-500 uppercase">Délai paiement</th>}
                      {payment_terms.escompte_percent != null && payment_terms.escompte_percent > 0 && <th className="text-right px-4 py-2 text-[10px] font-bold text-gray-500 uppercase">Escompte</th>}
                      {payment_terms.encours_max != null && payment_terms.encours_max > 0 && <th className="text-right px-4 py-2 text-[10px] font-bold text-gray-500 uppercase">Encours maximum</th>}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {payment_terms.mode_paiement && <td className="px-4 py-2.5 font-medium text-gray-900">{payment_terms.mode_paiement}</td>}
                      {payment_terms.delai_jours && (
                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">
                          {payment_terms.delai_jours} jours
                          {payment_terms.commentaire && <span className="text-xs text-gray-400 italic ml-1">({payment_terms.commentaire})</span>}
                        </td>
                      )}
                      {payment_terms.escompte_percent != null && payment_terms.escompte_percent > 0 && <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{payment_terms.escompte_percent} %</td>}
                      {payment_terms.encours_max != null && payment_terms.encours_max > 0 && <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{payment_terms.encours_max.toLocaleString('fr-FR')} €</td>}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile — carte */}
            <div className="md:hidden bg-white rounded-lg border border-gray-200 shadow-sm p-4" data-testid="payment-terms-mobile">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Conditions de paiement</h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                {payment_terms.mode_paiement && (
                  <div><span className="text-gray-500">Mode de paiement</span><p className="font-semibold text-gray-900">{payment_terms.mode_paiement}</p></div>
                )}
                {payment_terms.delai_jours && (
                  <div>
                    <span className="text-gray-500">Délai paiement</span>
                    <p className="font-semibold text-gray-900">{payment_terms.delai_jours} jours</p>
                    {payment_terms.commentaire && <p className="text-gray-400 italic">{payment_terms.commentaire}</p>}
                  </div>
                )}
                {payment_terms.escompte_percent != null && payment_terms.escompte_percent > 0 && (
                  <div><span className="text-gray-500">Escompte</span><p className="font-semibold text-gray-900">{payment_terms.escompte_percent} %</p></div>
                )}
                {payment_terms.encours_max != null && payment_terms.encours_max > 0 && (
                  <div><span className="text-gray-500">Encours maximum</span><p className="font-semibold text-gray-900">{payment_terms.encours_max.toLocaleString('fr-FR')} €</p></div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* ── Processus de commande ── */}
        {process_commande && (
          <section className="mb-6">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50"><h2 className="text-lg font-semibold text-gray-900">Processus de commande</h2></div>
              <div className="px-5 py-4"><p className="text-sm sm:text-base text-gray-700 whitespace-pre-line leading-relaxed">{process_commande}</p></div>
            </div>
          </section>
        )}

        {/* ── Contacts ── */}
        {displayContacts.length > 0 && (
          <section className="mb-6">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-900">{displayContacts.length > 1 ? 'Contacts' : 'Contact'}</h2>
              </div>
              <div className="px-5 py-4">
                <div className={displayContacts.length > 1 ? 'grid grid-cols-1 sm:grid-cols-2 gap-4' : ''}>
                  {displayContacts.map((contact, idx) => (
                    <div key={contact.id || idx} className={displayContacts.length > 1 ? 'p-4 bg-gray-50 rounded-lg' : ''}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-medium text-sm flex-shrink-0">
                          {contact.nom?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <span className="font-semibold text-gray-900">{contact.nom}</span>
                          {contact.fonction && <span className="ml-2 text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{contact.fonction}</span>}
                        </div>
                      </div>
                      <div className="space-y-1 ml-12">
                        {contact.email && <a href={`mailto:${contact.email}`} className="block text-sm text-blue-600 hover:underline">{contact.email}</a>}
                        {contact.telephone && <a href={`tel:${contact.telephone}`} className="block text-sm text-blue-600 hover:underline">{contact.telephone}</a>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default PartnerDetailContent;
